import { useEffect, useState } from 'react';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import {
  handleCommissionsInfoValidation,
  handleRpmAccountValidation,
  handleValidationStatusValidation,
  ProvisionalAsset,
  ProvisionalAssetUpdate,
  updateProvisionalAsset
} from '../../../store/ProvisionalAsset';
import { DetailPanelContainer } from '../../Compounds/DetailPanel/DetailPanelContainer';
import {
  provisionalAssetSectionsInfo,
  commissionsSectionInfo,
  assetValidationInfo,
  assetSectionMeta,
  provisionalAssetRpmAccountsInfo
} from './provisionalAssetHelpers';
import { TicketMiniTable } from '../../Compounds/Ticket/TicketMiniTable';
import { fetchTicketsByPAId } from '../../../store/Ticket';
import { RPMMiniTable } from '../../Compounds/RPM/RPMMiniTable';
import {
  fetchRpmAccountsByPAsset,
  getTallyRpmAccountsByPAssetLoadingSubject,
  getTallyRpmAccountsByPAssetSubject
} from '../../../store/RpmAccount';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { isData, isObserver } from '../../../store/User';
import { DetailsLinkCTA } from '../../Compounds/DetailsLinkCTA';
import { NewLinkDialog } from '../../Compounds/Linking/NewLinkDialog/NewLinkDialog';
import { deleteLink } from '../../../store/Link';
import { EntityType } from '../../../store/Search';
import { DetailViewValidationCard, ValidatorResult } from '../../Compounds/CardWithTitle';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { formatValidationMessages, handleRunValidator } from '../../Compounds/CardWithTitle/validationCardHelpers';

export interface ProvisionalAssetDetailsParams {
  data: ProvisionalAsset;
}

export const ProvisionalAssetDetails = ({ data }: ProvisionalAssetDetailsParams) => {
  const saveAction = (update: ProvisionalAssetUpdate) => updateProvisionalAsset(update, true);
  const [isLinkFormOpen, setIsLinkFormOpen] = useState<boolean>(false);
  const [validationStatusBySection, setValidationStatusBySection] = useState<Record<string, ValidatorResult>>({});
  const { setSnack } = useSnackbar();

  useEffect(() => {
    const messages = formatValidationMessages(validationStatusBySection);
    if (messages.length) {
      setSnack({
        message: messages.join(', '),
        type: 'warning',
        duration: 15000,
        open: true
      });
    }
  }, [JSON.stringify(validationStatusBySection)]);

  const cardActions = () => {
    return isObserver() || isData() ? <></> : <DetailsLinkCTA setIsFormOpen={() => setIsLinkFormOpen(true)} />;
  };

  return (
    <div
      className="space-y-4"
      data-testid="provisional-asset-details-drawer">
      <DetailViewValidationCard
        enableCollapse
        key="commission_detail"
        title="Commissions"
        dataTestId="commission-detail-container"
        data={commissionsSectionInfo(data)}
        saveAction={(d) => saveAction(d as ProvisionalAssetUpdate)}
        wrapperClassName="min-h-[25rem]"
        detailValidator={(d) =>
          handleRunValidator(
            'commissionsInfo',
            d,
            handleCommissionsInfoValidation,
            validationStatusBySection,
            setValidationStatusBySection
          )
        }
      />
      <DetailsComments
        id={data.id}
        entityType="provisional_asset"
      />
      <DetailViewCard
        enableCollapse
        key="provisional_asset_detail"
        title="Info">
        <div className="min-h-[25rem]">
          <DetailPanelContainer
            data={provisionalAssetSectionsInfo(data)}
            saveAction={(d) => saveAction(d as ProvisionalAssetUpdate)}
          />
        </div>
      </DetailViewCard>
      <DetailViewValidationCard
        cardActions={cardActions()}
        enableCollapse
        key="rpm_account_detail"
        title="RPM Accounts"
        dataTestId="rpm-account-detail-container"
        data={provisionalAssetRpmAccountsInfo(data)}
        detailValidator={(d) =>
          handleRunValidator(
            'rpmAccounts',
            d,
            handleRpmAccountValidation,
            validationStatusBySection,
            setValidationStatusBySection
          )
        }>
        <RPMMiniTable
          id={data.id}
          fetchFunction={fetchRpmAccountsByPAsset}
          removeFunction={deleteLink}
          dataSubject={getTallyRpmAccountsByPAssetSubject}
          loadingSubject={getTallyRpmAccountsByPAssetLoadingSubject}
          showRemove
        />
      </DetailViewValidationCard>
      <TicketMiniTable
        id={data.id}
        fetchFunction={fetchTicketsByPAId}
        association={{
          type: 'provisional_asset',
          body: {
            entityId: data.id
          }
        }}
        showCreate={true}
        objectType="provisionalAsset"
        objectData={data}
        objectLinkId={data.id}
        objectLinkDisplay={`Provisional Asset ${data.assetNumber}`}
        drawer
      />
      <DetailViewValidationCard
        enableCollapse
        key="asset-validation-card"
        title="Validation"
        dataTestId="validation-detail-container"
        data={assetValidationInfo(data)}
        saveAction={(d) => saveAction(d as ProvisionalAssetUpdate)}
        detailValidator={(d) =>
          handleRunValidator(
            'commissionsValidation',
            d,
            handleValidationStatusValidation,
            validationStatusBySection,
            setValidationStatusBySection
          )
        }
      />
      <DetailViewCard
        enableCollapse
        key="meta_detail"
        title="Meta">
        <div>
          <DetailPanelContainer
            dataTestId="meta_detail-container"
            data={assetSectionMeta(data)}
            saveAction={(d) => saveAction(d as ProvisionalAssetUpdate)}
          />
        </div>
      </DetailViewCard>
      <NewLinkDialog
        open={isLinkFormOpen}
        setOpen={setIsLinkFormOpen}
        asset={data}
        typeAsset={EntityType.PROVISIONAL_ASSET}
      />
    </div>
  );
};
